'use client';

// Global
import { useState, useRef, useEffect, useMemo, useCallback, useTransition } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import {
  getWidgetData,
  SearchWidgetItem,
  WidgetRequestData,
  Context,
  ComparisonFacetFilter,
} from '@sitecore-cloudsdk/search/browser';
import { tv } from 'tailwind-variants';

// Local
import { getTestProps } from 'lib/testing/utils';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getCtaStyle } from 'lib/utils/cta-utils';
import useDictionary from 'lib/hooks/useDictionary';
import { DICTIONARY_KEYS } from 'helpers/Constants/SiteConfig';
import SearchInputBox from 'helpers/Search/SearchInputBox';
import SearchSort, { SortOption } from 'helpers/Search/SearchSort';
import SearchPagination from 'helpers/Search/SearchPagination';
import { Facet } from 'helpers/Search/SearchFacetFilters';
import SkeletonCard from 'helpers/SkeletonCard/SkeletonCard';
import SkeletonFilterPanel from 'helpers/SkeletonCard/SkeletonFilterPanel';
import Select, { StylesConfig } from 'react-select';
import {
  getSelectStyles as getFormSelectStyles,
  DropdownIndicator,
} from 'helpers/FormFields/SelectField';
import { ArticleSearchProps } from 'components/authorable/shared/search/ArticleSearch';
import {
  mapResultsToTrackEntities,
  trackSearchFacetClick,
  trackSearchItemClick,
  trackSearchNavigationClick,
  trackSearchWidgetView,
} from 'lib/search/analytics';
import {
  ARTICLE_SEARCH_QUERY_PARAMS,
  buildArticleSearchFilter,
  normalizeSearchQueryParam,
} from 'lib/search/article-search-filters';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';

/** Subset of SDK facet type shape (not exported from `@sitecore-cloudsdk/search/browser`). */
type FacetTypeOptions = {
  name: string;
  exclude?: string[];
  max?: number;
  keyphrase?: string;
  minCount?: number;
};

type ArticleModel = {
  id: string;
  type?: string;
  title?: string;
  name?: string;
  url?: string;
  description?: string;
  date?: string;
  news_type?: string;
  image_url?: string;
  image_alt?: string;
  page_description?: string;
  source_id?: string;
};

type SearchResultsProps = {
  rfkId: string;
  sources: string[] | undefined;
};

type SearchSortItemProps = {
  label: string;
  name: string;
};

type SearchSortProps = {
  choices: SearchSortItemProps[];
};

interface SelectOptionType {
  value: string;
  label: string;
}

type SearchResponse = {
  widgets: Array<{
    content: ArticleModel[];
    total_item?: number;
    facet?: Array<{
      name: string;
      label?: string;
      type?: string;
      value?: Array<{
        id: string;
        text: string;
        count?: number;
      }>;
    }>;
    sort?: SearchSortProps;
  }>;
};

const ArticleSearchWidget = ({
  rfkId,
  sources,
  ...props
}: SearchResultsProps & ArticleSearchProps) => {
  const isEditing = useIsEditing();
  const { cardsCTAText, filterText, searchPlaceholder } = props.fields ?? {};
  const { getDictionaryValue } = useDictionary();
  const { isSearchInitialized } = useSearchInitialization();

  // Wrap post-fetch result commits in a transition so a parent <Suspense>
  // boundary (this widget sits under one) does not fall back to its empty
  // skeleton while React commits the new tree. Matches the pattern used by
  // `useCloudSearch` for ExpertList — using a synchronous commit here trips
  // the Suspense fallback and the skeleton state "wins" against loaded data.
  const [, startResultTransition] = useTransition();

  const {
    mainContainer,
    // titleSection,
    // title,
    searchSection,
    searchWrapper,
    filterCard,
    filterLabel,
    filterDropdowns,
    filterDropdown,
    resultsHeader,
    resultsCount,
    sortWrapper,
    container,
    header,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrow,
    heading,
    description,
    sectionDivider,
    footer,
    cta,
    imageWrapper,
    gridContainer,
    paginationContainer,
    noResultsContainer,
    noResultsText,
  } = TAILWIND_VARIANTS({ colCount: '3' });

  const [articles, setArticles] = useState<ArticleModel[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSort, setSelectedSort] = useState('');
  const [facets, setFacets] = useState<Facet[]>([]);
  const [sortOptions, setSortOptions] = useState<SortOption[]>([]);
  const [selectedFacets, setSelectedFacets] = useState<
    Array<{
      label: string;
      name: string;
      value: Array<{
        id: string;
        text: string;
        count?: number;
      }>;
    }>
  >([]);

  const widgetRef = useRef<HTMLDivElement>(null);
  const isSyncingUrlRef = useRef(false);
  const selectedFacetsRef = useRef(selectedFacets);
  const router = useRouter();
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const defaultSiteLocale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en';
  const itemsPerPage = 6;

  useEffect(() => {
    selectedFacetsRef.current = selectedFacets;
  }, [selectedFacets]);

  const getSingleQueryValue = useCallback((value: string | string[] | undefined) => {
    if (Array.isArray(value)) {
      return value[0] || '';
    }
    return value || '';
  }, []);

  const updateUrlParams = useCallback(
    (nextValues: { searchTag: string; page: number }) => {
      const sp = new URLSearchParams(searchParams.toString());
      const expertNameKey = ARTICLE_SEARCH_QUERY_PARAMS.expertName;
      const currentSearchTag = getSingleQueryValue(sp.get('searchTag') ?? undefined).trim();
      const currentExpertName =
        normalizeSearchQueryParam(sp.get(expertNameKey) ?? undefined) ?? '';
      const currentPageFromQuery = Number.parseInt(
        getSingleQueryValue(sp.get('page') ?? undefined),
        10
      );
      const currentPage =
        Number.isNaN(currentPageFromQuery) || currentPageFromQuery < 1 ? 1 : currentPageFromQuery;
      const normalizedSearchTag = nextValues.searchTag.trim();
      const normalizedPage = nextValues.page > 1 ? nextValues.page : 1;

      // Avoid route updates when query values are unchanged.
      // Also continue when expertName is present so clear/search can drop the deep-link param.
      if (
        currentSearchTag === normalizedSearchTag &&
        currentPage === normalizedPage &&
        !currentExpertName
      ) {
        return;
      }

      if (normalizedSearchTag) {
        sp.set('searchTag', normalizedSearchTag);
      } else {
        sp.delete('searchTag');
      }

      // User-driven search/clear replaces the expertName deep-link param.
      sp.delete(expertNameKey);

      if (normalizedPage > 1) {
        sp.set('page', String(normalizedPage));
      } else {
        sp.delete('page');
      }

      isSyncingUrlRef.current = true;
      const qs = sp.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [pathname, router, searchParams, getSingleQueryValue]
  );

  const updatePageParamInBrowserUrl = useCallback((page: number) => {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    window.history.replaceState(window.history.state, '', url.toString());
  }, []);

  // Get selected sort option in the format expected by API
  const getSelectedSortOption = useCallback(
    (selectOption: string) => {
      const selectedOption = sortOptions.filter(
        (option: SortOption) => option.value === selectOption
      );
      if (selectedOption.length > 0) {
        return [{ name: selectedOption[0]?.value }] as [{ name: string }, ...{ name: string }[]];
      }
      return undefined;
    },
    [sortOptions]
  );

  // Fetch data using Cloud SDK
  const fetchSearchData = useCallback(
    async (
      page: number = 1,
      searchQuery: string = '',
      sortValue: string = '',
      facetFilters: Array<{ name: string; value: Array<{ id: string; text: string }> }> = []
    ) => {
      try {
        setIsLoading(true);

        // sources is string[]; use it directly, filtering empty/whitespace ones
        const searchSourceIds = Array.isArray(sources)
          ? sources.filter((s) => typeof s === 'string' && s.trim() !== '')
          : [];

        // Create context for the search
        const context = new Context({
          locale: {
            language: defaultSiteLocale.split('-')[0] || 'en',
            country: defaultSiteLocale.split('-')[1]?.toUpperCase() || 'us',
          },
        });

        // Base type filter, or expertName query-param filter when present.
        // Other URL params (searchTag, page, facets) are unchanged.
        const filterConfig = buildArticleSearchFilter({
          expertName: getSingleQueryValue(
            searchParams.get(ARTICLE_SEARCH_QUERY_PARAMS.expertName) ?? undefined
          ),
        });

        // Build facet filters
        const facetTypeList =
          facetFilters.length > 0
            ? (facetFilters.map((facet) => ({
                name: facet.name,
                filter: {
                  type: 'or',
                  values: facet.value.map((v) => new ComparisonFacetFilter('eq', v.text)),
                },
              })) as unknown as [FacetTypeOptions, ...FacetTypeOptions[]])
            : undefined;

        // Build search widget configuration
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const widgetConfig: any = {
          limit: itemsPerPage,
          content: {},
          offset: (page - 1) * itemsPerPage,
          facet: {
            all: true,
            types: facetTypeList,
          },
          sort: {
            choices: true,
            value: getSelectedSortOption(sortValue),
          },
        };

        widgetConfig.filter = filterConfig;

        // Add query if search term exists
        if (searchQuery && searchQuery.trim()) {
          widgetConfig.query = {
            keyphrase: searchQuery.trim(),
          };
        }

        if (searchSourceIds.length > 0) {
          widgetConfig.sources = searchSourceIds;
        }

        const searchWidget = new SearchWidgetItem(
          'newsarticle',
          rfkId,
          widgetConfig,
          searchSourceIds.length > 0 ? (searchSourceIds as [string, ...string[]]) : undefined
        );

        // Fetch widget data
        const response = (await getWidgetData(
          new WidgetRequestData([searchWidget]),
          context
        )) as SearchResponse;

        if (response?.widgets?.[0]) {
          const widgetData = response.widgets[0];

          const nextArticles = widgetData.content || [];
          const nextTotalItems = widgetData.total_item || 0;

          const selectedTypeFacet = facetFilters.find((facet) => facet.name === 'type');
          const getSelectedTypeFacetFallback = (): Facet[] => {
            if (!selectedTypeFacet || selectedTypeFacet.value.length === 0) {
              return [];
            }

            return [
              {
                name: 'type',
                label: 'Content Type',
                values: selectedTypeFacet.value.map((value) => ({
                  id: value.id.toLowerCase(),
                  text: value.text,
                  count: 0,
                  selected: true,
                })),
              },
            ];
          };

          let nextFacets: Facet[] = [];

          // Process facets
          if (widgetData.facet && widgetData.facet.length > 0) {
            const processedFacets: Facet[] = widgetData.facet
              .filter((f) => f.name === 'type') // Filter for 'type' facet (content type)
              .map((facet) => {
                // Keep selection based on currently applied filters, not just response payload.
                const selectedFacet = facetFilters.find((sf) => sf.name === facet.name);
                const selectedValues = selectedFacet?.value || [];
                const selectedValueIds = selectedValues.map((v) => v.id.toLowerCase());
                const mappedValues =
                  facet.value?.map((v) => ({
                    id: v.id.toLowerCase(),
                    text: v.text,
                    count: v.count,
                    selected: selectedValueIds.includes(v.id.toLowerCase()),
                  })) || [];

                // Some responses (especially on paginated/filter slices) can omit selected options.
                // Re-add selected values so dropdown selection does not visually reset.
                selectedValues.forEach((selectedValue) => {
                  const exists = mappedValues.some(
                    (mappedValue) => mappedValue.id === selectedValue.id.toLowerCase()
                  );
                  if (!exists) {
                    mappedValues.push({
                      id: selectedValue.id.toLowerCase(),
                      text: selectedValue.text,
                      count: 0,
                      selected: true,
                    });
                  }
                });

                return {
                  name: facet.name,
                  label: facet.label || '',
                  values: mappedValues,
                };
              });

            nextFacets =
              processedFacets.length > 0 ? processedFacets : getSelectedTypeFacetFallback();
          } else {
            nextFacets = getSelectedTypeFacetFallback();
          }

          // Process sort options from API response
          const sortData = widgetData.sort;
          const sortChoices = sortData?.choices;

          let nextSortOptions: SortOption[] = [];
          let nextSelectedSort: string | null = null;

          if (sortChoices && sortChoices.length > 0) {
            // Transform sort data to match SearchSort format
            nextSortOptions = sortChoices.map((choice: SearchSortItemProps) => ({
                id: choice.name,
                label: choice.label,
                value: choice.name,
            }));

            // Set default sort if not already set
            if (!selectedSort && nextSortOptions.length > 0) {
              nextSelectedSort = nextSortOptions[0].value;
            }
          }

          // Commit post-fetch state inside a transition (matching ExpertList's
          // `useCloudSearch` pattern). Using `flushSync` here forced an urgent
          // commit that tripped a parent <Suspense> boundary into rendering its
          // empty fallback, which left the visible skeleton "winning" over the
          // loaded data even though the React state itself was correct.
          startResultTransition(() => {
            setArticles(nextArticles);
            setTotalItems(nextTotalItems);
            setFacets(nextFacets);
            setSortOptions(nextSortOptions);
            if (nextSelectedSort !== null) {
              setSelectedSort(nextSelectedSort);
          }
            setIsLoading(false);
            setHasInitialLoad(true);
          });

          void trackSearchWidgetView({
            widgetId: rfkId,
            entities: mapResultsToTrackEntities(nextArticles, 'newsarticle'),
            request: {
              keyword: searchQuery?.trim() || undefined,
              numResults: nextArticles.length,
              totalResults: nextTotalItems,
              numRequested: itemsPerPage,
              pageNumber: page,
              pageSize: itemsPerPage,
            },
          });
          return;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[ArticleSearchWidget] fetchSearchData error', error);
        startResultTransition(() => {
        setArticles([]);
        setTotalItems(0);
        setFacets([]);
        setIsLoading(false);
          setHasInitialLoad(true);
        });
        return;
      }
      // Reached only when the response did not contain a widget payload.
      startResultTransition(() => {
        setIsLoading(false);
        setHasInitialLoad(true);
      });
    },
    [
      rfkId,
      defaultSiteLocale,
      sources,
      itemsPerPage,
      getSelectedSortOption,
      selectedSort,
      searchParams,
      getSingleQueryValue,
    ]
  );

  // Check query parameters and auto-populate/search, or initial data fetch
  useEffect(() => {
    if (!isSearchInitialized) {
      return;
    }

    if (isSyncingUrlRef.current) {
      isSyncingUrlRef.current = false;
      return;
    }

    const searchTagValue = getSingleQueryValue(searchParams.get('searchTag') ?? undefined);
    const expertNameValue =
      normalizeSearchQueryParam(
        searchParams.get(ARTICLE_SEARCH_QUERY_PARAMS.expertName) ?? undefined
      ) ?? '';
    // Prefer searchTag; fall back to expertName so deep links show in the input.
    const displaySearchTerm = searchTagValue || expertNameValue;
    const pageFromQuery = Number.parseInt(
      getSingleQueryValue(searchParams.get('page') ?? undefined),
      10
    );
    const initialPage = Number.isNaN(pageFromQuery) || pageFromQuery < 1 ? 1 : pageFromQuery;

    setSearchTerm(displaySearchTerm);
    setCurrentPage(initialPage);
    // Keyphrase search still uses searchTag only; expertName is applied as a filter.
    fetchSearchData(initialPage, searchTagValue, selectedSort, selectedFacetsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), getSingleQueryValue, isSearchInitialized]);

  // Handle search
  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setCurrentPage(1);
      fetchSearchData(1, term, selectedSort, selectedFacets);
      updateUrlParams({
        searchTag: term,
        page: 1,
      });
    },
    [fetchSearchData, selectedSort, selectedFacets, updateUrlParams]
  );

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setCurrentPage(1);
    fetchSearchData(1, '', selectedSort, selectedFacets);
    updateUrlParams({
      searchTag: '',
      page: 1,
    });
  }, [fetchSearchData, selectedSort, selectedFacets, updateUrlParams]);

  // Handle sort change
  const handleSortChange = useCallback(
    (_optionId: string, value: string) => {
      setSelectedSort(value);
      fetchSearchData(currentPage, searchTerm, value, selectedFacetsRef.current);
    },
    [currentPage, searchTerm, fetchSearchData]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchSearchData(page, searchTerm, selectedSort, selectedFacetsRef.current);
      updatePageParamInBrowserUrl(page);
      void trackSearchNavigationClick({
        widgetId: rfkId,
        itemPosition: page,
      });
      // Scroll to top of results
      if (widgetRef.current) {
        widgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [searchTerm, selectedSort, fetchSearchData, updatePageParamInBrowserUrl, rfkId]
  );

  // Handle filter change (for dropdown selects)
  const handleFilterChange = useCallback(
    (facetName: string, selectedOption: SelectOptionType | null) => {
      const selectedValueId = selectedOption?.value || '';
      const selectedValueText = selectedOption?.label || '';
      let updatedFacets = [...selectedFacets];

      // If "All" is selected (empty value), remove the facet
      if (!selectedValueId || selectedValueId === '') {
        updatedFacets = updatedFacets.filter((f) => f.name !== facetName);
      } else {
        // Find the facet
        const facetIndex = updatedFacets.findIndex((f) => f.name === facetName);
        const newFacet = {
          name: facetName,
          label: 'Content Type',
          value: [{ id: selectedValueId.toLowerCase(), text: selectedValueText }],
        };

        if (facetIndex >= 0) {
          // Update existing facet
          updatedFacets[facetIndex] = newFacet;
        } else {
          // Add new facet
          updatedFacets = [...updatedFacets, newFacet];
        }

        void trackSearchFacetClick({
          widgetId: rfkId,
          filters: [
            {
              name: facetName,
              title: 'Content Type',
              value: selectedValueText,
              valuePosition: 0,
              displayName: selectedValueText,
              facetPosition: 0,
            },
          ],
          request: {
            keyword: searchTerm?.trim() || undefined,
            pageNumber: 1,
            pageSize: itemsPerPage,
          },
        });
      }

      setCurrentPage(1);
      setSelectedFacets(updatedFacets);
      selectedFacetsRef.current = updatedFacets;
      fetchSearchData(1, searchTerm, selectedSort, updatedFacets);
      if (currentPage !== 1) {
        updatePageParamInBrowserUrl(1);
      }
    },
    [
      selectedFacets,
      searchTerm,
      selectedSort,
      currentPage,
      fetchSearchData,
      updatePageParamInBrowserUrl,
      rfkId,
      itemsPerPage,
    ]
  );

  const handleArticleClick = useCallback(
    (article: ArticleModel, index: number) => {
      if (!article?.id) {
        return;
      }
      void trackSearchItemClick({
        widgetId: rfkId,
        entity: {
          id: article.id,
          entity: 'newsarticle',
          uri: article.url,
          sourceId: article.source_id,
        },
        itemPosition: index,
        request: {
          keyword: searchTerm?.trim() || undefined,
          numResults: articles.length,
          totalResults: totalItems,
          pageNumber: currentPage,
          pageSize: itemsPerPage,
        },
      });
    },
    [rfkId, searchTerm, articles.length, totalItems, currentPage, itemsPerPage]
  );

  // Memoized articles rendering for performance
  const renderedArticles = useMemo(() => {
    return articles.map((article, index) => {
      return (
        <article
          key={article.id || index}
          className={container()}
          data-component="authorable/shared/lists/carditem"
          {...editingHydrationProps(isEditing)}
          id={`article-${article.id || index}`}
          {...getTestProps(`card-item-${article.id || index}`)}
        >
          {/* Header/Image Section */}
          <div className={header()}>
            {article?.image_url ? (
              <ImageWrapper
                field={{
                  value: {
                    src: article?.image_url,
                    alt: article?.image_alt ? article?.image_alt : article?.name,
                  },
                }}
                className={imageWrapper()}
                priority
                {...getTestProps(`image`)}
              />
            ) : (
                <ImageWrapper
                  field={{
                    value: {
                      src: '/assets/profile_placeholder.png',
                      alt: 'No image',
                    },
                  }}
                  className={imageWrapper()}
                  {...getTestProps(`related-profile-image-placeholder`)}
                />
            )}
          </div>

          {/* Body/Content Section */}
          <div className={body()}>
            <div className={contentWrapper()}>
              {/* Content */}
              <div className={content()}>
                {/* Heading with Eyebrow */}
                <div className={headingWithEyebrow()}>
                  {article?.news_type && (
                    <div className={eyebrow()} {...getTestProps(`eyebrow`)}>
                      {article.news_type}
                    </div>
                  )}
                  {article?.name && (
                    <h3 className={heading()} {...getTestProps(`heading`)}>
                      {article.name}
                    </h3>
                  )}
                </div>

                {/* Description */}
                {article?.page_description && (
                  <div className={description()} {...getTestProps(`description`)}>
                    {article.page_description}
                  </div>
                )}

                {/* Section Divider */}
                {article?.url && <span className={sectionDivider()}></span>}
              </div>

              {/* Footer/CTA Section */}
              {article?.url && (
                <div className={footer()}>
                  <LinkWrapper
                    ctaComponentClass="component-card-item-button-2-color"
                    className={cta()}
                    ctaStyle={getCtaStyle({}, 'secondary')}
                    field={{
                      value: {
                        href: article.url,
                        text: cardsCTAText?.value,
                      },
                    }}
                    ctaIcon="arrow-right"
                    onClick={() => handleArticleClick(article, index)}
                    {...getTestProps(`primary-cta`)}
                  />
                </div>
              )}
            </div>
          </div>
        </article>
      );
    });
  }, [
    articles,
    container,
    header,
    imageWrapper,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrow,
    heading,
    description,
    sectionDivider,
    footer,
    cta,
    cardsCTAText,
    handleArticleClick,
  ]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const hasResults = totalItems > 0 || articles.length > 0;

  // Transform facets to dropdown options
  const getFilterOptions = useCallback((facet: Facet): SelectOptionType[] => {
    const options: SelectOptionType[] = [
      {
        value: '',
        label: `All ${facet.label || facet.name}`,
      },
      ...(facet.values?.map((value) => ({
        value: value.id.toLowerCase(),
        label: value.text,
      })) || []),
    ];
    return options;
  }, []);

  // Get selected filter value
  const getSelectedFilterValue = useCallback(
    (facetName: string, options: SelectOptionType[]): SelectOptionType => {
      const selectedFacet = selectedFacets.find((sf) => sf.name === facetName);
      if (selectedFacet && selectedFacet.value.length > 0) {
        const selectedValue = selectedFacet.value[0];
        const selectedId = selectedValue?.id?.toLowerCase() || '';
        const selectedText = selectedValue?.text?.toLowerCase() || '';
        return (
          options.find(
            (opt) =>
              opt.value.toLowerCase() === selectedId || opt.label.toLowerCase() === selectedText
          ) || options[0]
        );
      }
      return options[0]; // Return "All" option if nothing selected
    },
    [selectedFacets]
  );

  // Get content type facet
  const contentTypeFacet = facets.find((f) => f.name === 'type');
  const contentTypeOptions = useMemo(
    () => (contentTypeFacet ? getFilterOptions(contentTypeFacet) : []),
    [contentTypeFacet, getFilterOptions]
  );

  return (
    <div
      ref={widgetRef}
      id="article-search-cards"
      {...getTestProps(`article-search-cards`)}
      className={mainContainer()}
    >
      {/* Search Section */}
      <div className={searchSection()}>
        <div className={searchWrapper()}>
          <SearchInputBox
            entity="newsarticle"
            suggestionName="news_list_auto_suggester"
            value={searchTerm}
            placeholder={searchPlaceholder?.value}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            widgetId={rfkId}
            sources={sources}
            showSuggestions={true}
          />
        </div>

        {/* Filter Card - Show skeleton only on initial load, otherwise show actual filters or nothing */}
        {!hasInitialLoad ? (
          <SkeletonFilterPanel filterCount={1} />
        ) : contentTypeFacet ? (
          <div className={filterCard()}>
            <p className={filterLabel()}>{filterText?.value}</p>
            <div className={filterDropdowns()}>
              <div className={filterDropdown()}>
                <Select<SelectOptionType>
                  inputId="filter-content-type"
                  value={getSelectedFilterValue('type', contentTypeOptions)}
                  onChange={(option) => handleFilterChange('type', option)}
                  options={contentTypeOptions}
                  styles={
                    getFormSelectStyles(false, false) as unknown as StylesConfig<SelectOptionType>
                  }
                  components={{ DropdownIndicator }}
                  classNamePrefix="react-select"
                  aria-label="Filter by content type"
                  isSearchable={false}
                  {...getTestProps('filter-content-type')}
                />
              </div>
              {/* Placeholder for second facet (service areas) - will be implemented later */}
            </div>
          </div>
        ) : null}
      </div>

      {/* Results Header */}
      {hasInitialLoad && hasResults && (
        <div className={resultsHeader()}>
          <div className={resultsCount()}>
            {totalItems}{' '}
            {totalItems === 1
              ? getDictionaryValue(DICTIONARY_KEYS.ARTICLE_SEARCH.RESULT) || 'result'
              : getDictionaryValue(DICTIONARY_KEYS.ARTICLE_SEARCH.RESULTS) || 'results'}
            {searchTerm && ` for "${searchTerm}"`}
          </div>
          <div className={sortWrapper()}>
            <SearchSort
              options={sortOptions}
              selectedOption={selectedSort}
              onSortChange={handleSortChange}
              tailwindVariants={{
                position: 'ArticleSearch',
              }}
            />
          </div>
        </div>
      )}

      {/* Results Grid */}
      {isLoading || !hasInitialLoad ? (
        <div className={gridContainer()}>
          {[...Array(itemsPerPage)].map((_, index) => (
            <SkeletonCard key={index} variant="articleSearch" orientation="vertical" />
          ))}
        </div>
      ) : hasResults ? (
        <>
          <div className={gridContainer()}>{renderedArticles}</div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={paginationContainer()}>
              <SearchPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                showPageNumbers={true}
                maxVisiblePages={5}
              />
            </div>
          )}
        </>
      ) : (
        <div className={noResultsContainer()}>
          <div className={noResultsText()}>
            {getDictionaryValue(DICTIONARY_KEYS.ARTICLE_SEARCH.NO_RESULTS) || 'No results found'}
            {searchTerm && ` for "${searchTerm}"`}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleSearchWidget;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    colCount: '3',
  },
  slots: {
    mainContainer: [
      'w-full',
      'flex',
      'flex-col',
      'gap-component-section-container-gutter',
      'items-center',
      //'px-component-section-container-padding-x',
      // 'py-component-section-container-padding-y',
    ],
    searchSection: [
      'w-full',
      'flex',
      'flex-col',
      'gap-spacing-spacing-24',
      'items-start',
      'max-w-[817px]',
    ],
    searchWrapper: ['w-full'],
    filterCard: [
      'w-full',
      'bg-color-bg-white',
      'flex',
      'flex-col',
      'gap-spacing-spacing-8',
      'p-spacing-spacing-16',
      'rounded-border-radius-radius-4',
      'shadow-color-black-alpha-10',
      'shadow-[0px_0px_8px_0px]',
    ],
    filterLabel: [
      'font-typography-font-family-body',
      'font-[600]',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'text-color-text-dark',
      'text-left',
    ],
    filterDropdowns: [
      'w-full',
      'flex',
      'flex-col',
      'md:flex-row',
      'gap-space-between-less',
      'items-start',
    ],
    filterDropdown: ['w-full', 'md:flex-1'],
    resultsHeader: [
      'relative',
      'w-full',
      'flex',
      'flex-col',
      'md:flex-row',
      'md:justify-between',
      'md:items-center',
      'gap-space-between-base',
      'justify-start',
      'items-start',
      'md:items-center',
    ],
    resultsCount: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
      'text-color-text-dark',
      'font-bold',
    ],
    sortWrapper: ['flex'],
    container: [
      'flex',
      'w-full',
      'h-full',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'bg-component-card-item-surface',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
    ],
    header: [
      'relative',
      'min-h-max',
      'w-full',
      'overflow-hidden',
      'aspect-[1.61/1]',
      'text-color-fill-brand-1',
      'transition',
      'duration-300',
      'ease-in-out',
      'group',
      'pt-component-article-card-portrait-image-padding-top',
      'px-component-article-card-portrait-image-padding-x',
      'pb-component-article-card-portrait-image-padding-bottom',
    ],
    body: [
      'flex',
      'flex-row',
      'items-start',
      'text-left',
      'h-auto',
      'lg:h-full',
      'lg:justify-between',
      'flex-1',
    ],
    contentWrapper: [
      'flex',
      'flex-col',
      'w-full',
      'gap-spacing-spacing-24',
      'text-left',
      'h-auto',
      'lg:h-full',
      'justify-start',
      'lg:justify-between',
      'py-component-card-item-landscape-content-padding-y',
      'px-component-card-item-landscape-content-padding-x',
    ],
    content: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-24', 'text-left'],
    headingWithEyebrow: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-4'],
    eyebrow: [
      'text-component-card-item-eyebrow',
      'font-typography-font-family-body',
      'text-typography-font-size-body-xSmall',
      'mb-spacing-spacing-4',
      'font-semibold',
      'leading-[150%]',
    ],
    heading: [
      'font-typography-font-family-header',
      'font-normal',
      'text-typography-font-size-medium',
      'text-component-card-item-title',
      'leading-typography-line-height-medium',
    ],
    description: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
      'mb-spacing-spacing-8',
    ],
    sectionDivider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    footer: [
      'flex',
      'flex-wrap',
      'flex-col',
      'gap-spacing-spacing-8',
      'items-start',
      'justify-center',
      'w-full',
      'mt-auto',
    ],
    cta: [
      'flex',
      'gap-spacing-spacing-8',
      'items-center',
      'justify-center',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      '!w-auto',
    ],
    imageWrapper: [
      'w-full',
      'h-full',
      'aspect-[1.61/1]',
      'object-cover',
      'rounded-component-article-card-image-radius',
    ],
    // Grid container for the list - 3 columns
    gridContainer: [
      'grid',
      'grid-cols-1',
      'md:grid-cols-2',
      'lg:grid-cols-3',
      'gap-y-space-between-base',
      'gap-x-layout-base-gutter',
      'w-full',
    ],
    // Pagination container
    paginationContainer: ['flex', 'justify-center'],
    // No results container
    noResultsContainer: [
      'w-full',
      'flex',
      'justify-center',
      'items-center',
      'py-spacing-spacing-64',
    ],
    noResultsText: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'text-color-text-text-secondary',
      'font-semibold',
      'leading-typography-line-height-body-large',
    ],
  },
  variants: {
    colCount: {
      '3': {
        header: ['aspect-[1.61/1]'],
        container: ['h-full', 'flex-col'],
        body: ['flex-col', 'h-full', 'items-start', 'text-left'],
        content: ['text-left'],
        contentWrapper: ['h-full', 'text-left', 'gap-spacing-spacing-24'],
        footer: ['mt-auto'],
        imageWrapper: ['rounded-b-none'],
      },
    },
  },
});
